import React, { useState } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { format, parseISO } from 'date-fns';
import {
  MuiPickersUtilsProvider,
  DatePicker,
} from '@material-ui/pickers';
import {
  Button,
  Chip,
  Collapse,
  Divider,
  Icon,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import NumberFormat from 'react-number-format';
import { TextFormattedNumber } from 'components';


const subscriptionStatus = {
  active: 'Active',
  paused: 'Paused',
  canceled: 'Canceled',
  complete: 'Complete',
  payment_failed: 'Payment Failed',
  deleted_customer: 'Deleted Customer'
};

const subtotalPreview = (value) => {
  return (
    <TextFormattedNumber
      value={value}
      prefix="$"
    />
  );
};

const FilterDrawer = (
  {setOpenFilter, setFilterConditions, filterConditions}: IFilterDrawerProps
) => {
  const [expandedDate, toggleExpandedDate] = useState(false);
  const [expandedLastRun, toggleExpandedLastRun] = useState(false);
  const [expandedNextRun, toggleExpandedNextRun] = useState(false);
  const [expandedStatus, toggleExpandedStatus] = useState(false);
  const [expandedSubtotal, toggleExpandedSubtotal] = useState(false);
  const [conditions, setConditions] = useState({
    created_from: {
      field: 'created_at',
      value: filterConditions.created_from?.value || '',
      conditionType: 'gt'
    },
    created_to: {
      field: 'created_at',
      value: filterConditions.created_to?.value || '',
      conditionType: 'lt'
    },
    last_run_from: {
      field: 'last_run',
      value: filterConditions.last_run_from?.value || '',
      conditionType: 'gt'
    },
    last_run_to: {
      field: 'last_run',
      value: filterConditions.last_run_to?.value || '',
      conditionType: 'lt'
    },
    next_run_from: {
      field: 'next_run',
      value: filterConditions.next_run_from?.value || '',
      conditionType: 'gt'
    },
    next_run_to: {
      field: 'next_run',
      value: filterConditions.next_run_to?.value || '',
      conditionType: 'lt'
    },
    status: {
      field: 'status',
      value: filterConditions.status?.value || '',
      conditionType: 'eq'
    },
    subtotal_from: {
      field: 'subtotal',
      value: filterConditions.subtotal?.value || '',
      conditionType: 'gt'
    },
    subtotal_to: {
      field: 'subtotal',
      value: filterConditions.subtotal?.value || '',
      conditionType: 'lt'
    }
  });

  const handleChangeFilter = (field, value, conditionType, name) => {
    setConditions((conditions) => (
      {
        ...conditions,
        [name]: {
          field,
          value,
          conditionType
        }
      }
    ));
  };

  const setConvertedDate = (value) => {
    const date = new Date(value);
    return date.toUTCString();
  };

  const handleClearDate = (event, name) => {
    event.preventDefault();
    event.stopPropagation();
    setConditions({
      ...conditions,
      [name]: {
        ...conditions[name],
        value: ''
      }
    });
  };

  const handleApplyFilter = () => {
    const nonEmptyFilters = {};

    for (const field in conditions) {
      if (conditions[field].value) {
        nonEmptyFilters[field] = conditions[field];
      }
    }

    setFilterConditions(nonEmptyFilters);
    setOpenFilter(false);
  };

  const isDatesError = conditions?.created_to.value < conditions?.created_from?.value;
  const isLastRunDateError = conditions?.last_run_to.value < conditions?.last_run_from?.value;
  const isNextRunDateError = conditions?.next_run_to.value < conditions?.next_run_from?.value;
  const isSubtotalError = conditions?.subtotal_to.value < conditions?.subtotal_from?.value;

  const isError = isDatesError || isLastRunDateError || isNextRunDateError || isSubtotalError;

  return (
    <div className="px-4 pt-4 min-h-full-screen flex-column justify-between">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Typography variant="h5" className="text-primary uppercase">Filter by:</Typography>
          <IconButton onClick={() => setOpenFilter(false)}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </div>
        <div className="flex items-center flex-wrap mb-2">
          {Object.values(conditions).map((filterItem, index) => {
            if (!filterItem.value) {
              return null;
            }

            let label = filterItem?.value;
            let fieldName = filterItem.field;

            if (filterItem.field === 'status') {
              label = subscriptionStatus[filterItem.value];
            }

            if (filterItem.field === 'created_at') {
              if (filterItem.conditionType === 'gt') {
                label = `Purchased From: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'created_from';
              }

              if (filterItem.conditionType === 'lt') {
                label = `Purchased To: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'created_to';
              }
            }

            if (filterItem.field === 'last_run') {
              if (filterItem.conditionType === 'gt') {
                label = `Last Run From: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'last_run_from';
              }

              if (filterItem.conditionType === 'lt') {
                label = `Last Run To: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'last_run_to';
              }
            }

            if (filterItem.field === 'next_run') {
              if (filterItem.conditionType === 'gt') {
                label = `Next Run From: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'next_run_from';
              }

              if (filterItem.conditionType === 'lt') {
                label = `Next Run To: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
                fieldName = 'next_run_to';
              }
            }

            if (filterItem.field === 'subtotal') {
              if (filterItem.conditionType === 'gt') {
                label = subtotalPreview(filterItem.value);
                fieldName = 'subtotal_from';
              }

              if (filterItem.conditionType === 'lt') {
                label = subtotalPreview(filterItem.value);
                fieldName = 'subtotal_to';
              }
            }

            return (
              <div key={`filter-${index}`} className="mr-2 mb-2">
                <Chip
                  label={label}
                  onDelete={() => {
                    setConditions({
                      ...conditions,
                      [fieldName]: {
                        ...conditions[fieldName],
                        value: ''
                      }
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedDate((expandedDate) => !expandedDate)}
        >
          <Typography variant="h6">Purchased Date</Typography>
          {expandedDate ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedDate} timeout="auto">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="created_from"
              label="From"
              className="mt-5"
              value={
                conditions?.created_from?.value
                  ? setConvertedDate(conditions.created_from.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'created_at',
                `${format(date, 'yyyy-MM-dd 00:00:00')}`,
                'gt',
                'created_from'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'created_from')}
                  >
                    close
                  </Icon>
                )
              }}
            />
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="created_to"
              className="mt-5"
              label="To"
              value={
                conditions?.created_to?.value
                  ? setConvertedDate(conditions.created_to.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'created_at',
                `${format(date, 'yyyy-MM-dd 23:59:59')}`,
                'lt',
                'created_to'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'created_to')}
                  >
                    close
                  </Icon>
                )
              }}
              error={isDatesError}
              helperText={
                isDatesError
                && 'End Date can\'t be less than Start Date'
              }
            />
          </MuiPickersUtilsProvider>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedLastRun((expandedLastRun) => !expandedLastRun)}
        >
          <Typography variant="h6">Last Run (Date)</Typography>
          {expandedLastRun ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedLastRun} timeout="auto">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="last_run_from"
              label="From"
              className="mt-5"
              value={
                conditions?.last_run_from?.value
                  ? setConvertedDate(conditions.last_run_from.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'last_run',
                `${format(date, 'yyyy-MM-dd 00:00:00')}`,
                'gt',
                'last_run_from'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'last_run_from')}
                  >
                    close
                  </Icon>
                )
              }}
            />
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="last_run_to"
              className="mt-5"
              label="To"
              value={
                conditions?.last_run_to?.value
                  ? setConvertedDate(conditions.last_run_to.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'last_run',
                `${format(date, 'yyyy-MM-dd 23:59:59')}`,
                'lt',
                'last_run_to'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'last_run_to')}
                  >
                    close
                  </Icon>
                )
              }}
              error={isLastRunDateError}
              helperText={
                isLastRunDateError
                && 'End Date can\'t be less than Start Date'
              }
            />
          </MuiPickersUtilsProvider>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedNextRun((expandedNextRun) => !expandedNextRun)}
        >
          <Typography variant="h6">Next Run (Date)</Typography>
          {expandedNextRun ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedNextRun} timeout="auto">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disablePast
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="next_run_from"
              label="From"
              className="mt-5"
              value={
                conditions?.next_run_from?.value
                  ? setConvertedDate(conditions.next_run_from.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'next_run',
                `${format(date, 'yyyy-MM-dd 00:00:00')}`,
                'gt',
                'next_run_from'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'next_run_from')}
                  >
                    close
                  </Icon>
                )
              }}
            />
            <DatePicker
              disablePast
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="next_run_to"
              className="mt-5"
              label="To"
              value={
                conditions?.next_run_to?.value
                  ? setConvertedDate(conditions.next_run_to.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'next_run',
                `${format(date, 'yyyy-MM-dd 23:59:59')}`,
                'lt',
                'next_run_to'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'next_run_to')}
                  >
                    close
                  </Icon>
                )
              }}
              error={isNextRunDateError}
              helperText={
                isNextRunDateError
                && 'End Date can\'t be less than Start Date'
              }
            />
          </MuiPickersUtilsProvider>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedStatus((expandedStatus) => !expandedStatus)}
        >
          <Typography variant="h6">Status</Typography>
          {expandedStatus ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedStatus} timeout="auto">
          <FormControl component="div">
            <RadioGroup
              aria-label="status"
              name="status"
              value={conditions?.status?.value || ''}
              onChange={({target: {value, name}}) => handleChangeFilter(name, value, 'eq', name)}
            >
              {Object.keys(subscriptionStatus).map((status) => (
                <FormControlLabel
                  key={`subscription-status-${status}`}
                  value={status}
                  control={<Radio color="primary" size="small" />}
                  label={subscriptionStatus[status]}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedSubtotal((expandedSubtotal) => !expandedSubtotal)}
        >
          <Typography variant="h6">Subtotal</Typography>
          {expandedSubtotal ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedSubtotal} timeout="auto">
          <NumberFormat
            variant="outlined"
            name="subtotal_from"
            label="From"
            className="mt-5 w-full"
            prefix="$"
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
            value={conditions?.subtotal_from?.value || ''}
            onValueChange={(values) => handleChangeFilter(
              'subtotal',
              values.floatValue,
              'gt',
              'subtotal_from'
            )
            }
            customInput={TextField}
          />
          <NumberFormat
            variant="outlined"
            name="subtotal_to"
            label="To"
            className="mt-5 w-full"
            prefix="$"
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
            value={conditions?.subtotal_to?.value || ''}
            onValueChange={(values) => handleChangeFilter(
              'subtotal',
              values.floatValue,
              'lt',
              'subtotal_to'
            )
            }
            customInput={TextField}
            error={isSubtotalError}
            helperText={
              isSubtotalError
              && 'Subtotal To can\'t be less than From'
            }
          />
        </Collapse>
        <Divider className="my-5" />
      </div>
      <div className="flex items-center justify-between pb-4">
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="mr-2 uppercase"
          onClick={handleApplyFilter}
          disabled={isError}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          fullWidth
          className="uppercase"
          onClick={() => {
            setFilterConditions({});
            setOpenFilter(false);
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default FilterDrawer;
